/**
 * staffAuthApi — typed controller for chrilantech STAFF login (ADR-003 D8).
 *
 * The BFF lives at 127.0.0.1:9103 behind the host's nginx on /api/staff-auth/*.
 * The browser holds the SIH HTTP-only Secure SameSite session cookie
 * (SIHSESSION) — the same shape as the customer gateway but with a
 * different cookie name (no namespace collision in document.cookie).
 *
 * Flow (per ADR-001 — preserved verbatim on /api/staff-auth/*):
 *   POST /api/staff-auth/login   {username, password} → 202 {loginRef, displayName, message}
 *   POST /api/staff-auth/verify  {loginRef, otp}    → 200 {user}; SIHSESSION cookie set
 *   POST /api/staff-auth/logout                       → 200 {loggedOut, remoteRevocationFailed}
 *   GET  /api/staff-auth/me                          → 200 {user}; 401 → null
 *
 * CSRF: Spring Security's CookieCsrfTokenRepository (XSRF-TOKEN cookie +
 * X-XSRF-TOKEN header on POSTs). The browser sends the cookie automatically;
 * this controller explicitly echoes its value as the double-submit header.
 * Customer-gateway token names are separate and must never be substituted.
 *
 * The authStore wires the OTP step explicitly because the SPA owns the form:
 * login() returns the opaque loginRef + the OTP prompt; verify(loginRef, otp)
 * establishes the SIHSESSION cookie and returns the display profile.
 */

import { endpoints, request } from './apiClient'
import { ApiError } from '@domain/types'
import { readCsrfCookie } from '@lib/cookies'
import {
  AuthApiError, type AuthStep,
  type AuthUser,
  type EmployeeLoginResult,
  mapAuthErrorCode,
} from '@domain/auth'

interface BffLoginAcceptedResponse {
  readonly loginRef?: unknown
  readonly displayName?: unknown
  readonly message?: unknown
}

interface BffMeResponse {
  readonly user?: unknown
}

interface BffStaffUser {
  readonly id?: unknown
  readonly displayName?: unknown
  readonly officeName?: unknown
}

/** Echo the BFF-issued token on staff POSTs; never invent one or use the customer token. */
function staffCsrfHeaders(): Record<string, string> {
  const token = readCsrfCookie('XSRF-TOKEN')
  return token === null ? {} : { 'X-XSRF-TOKEN': token }
}

/**
 * Validate + map the BFF staff contract to the shared AuthUser.
 *
 * The contract of record is the BFF's SessionUserResponse (ADR-001: the SPA
 * sees ONLY id + displayName + officeName — the TicketApp Username and phone
 * number are redacted server-side). Verified live on 2026-09-19:
 *   {"user":{"id":"1","displayName":"Christo Naude","officeName":"Lusaka"}}
 * The id is TicketApp's identifier (a proven positive integer upstream) that
 * the BFF carries as a JSON string; it is normalised to a number here with a
 * named contract violation — never a NaN — when it is not one. `officeName`
 * maps to AuthUser.role (the org tag AppHeader renders on the kind badge).
 * No username exists on the staff surface, so AuthUser.username stays unset.
 */
export function mapStaffUser(value: unknown, what = 'user'): AuthUser {
  if (value === null || typeof value !== 'object') {
    throw new ApiError(502, 'INVALID_RESPONSE', `Staff auth response is missing the ${what} object.`)
  }
  const obj = value as BffStaffUser
  const rawId = obj.id
  const id =
    typeof rawId === 'number'
      ? rawId
      : typeof rawId === 'string' && rawId.trim() !== ''
        ? Number(rawId)
        : Number.NaN
  if (!Number.isSafeInteger(id) || id <= 0) {
    throw new ApiError(502, 'INVALID_RESPONSE', `Staff auth response ${what}.id is missing or not a positive integer.`)
  }
  if (typeof obj.displayName !== 'string') {
    throw new ApiError(502, 'INVALID_RESPONSE', `Staff auth response ${what} is missing displayName.`)
  }
  if (typeof obj.officeName !== 'string') {
    throw new ApiError(502, 'INVALID_RESPONSE', `Staff auth response ${what} is missing officeName.`)
  }
  return {
    id,
    displayName: obj.displayName,
    role: obj.officeName,
  }
}

/**
 * Step 1 of the employee flow: POST /api/staff-auth/login.
 *
 * Returns 202 with an opaque loginRef; the SPA prompts for the OTP and
 * calls verify() to exchange the ref for the SIHSESSION cookie.
 *
 * Throws AuthApiError with a pre-classified reason on any failure.
 */
export async function login(username: string, password: string, signal?: AbortSignal): Promise<EmployeeLoginResult> {
  try {
    const response = await request<BffLoginAcceptedResponse>(endpoints.staffAuthLogin, {
      method: 'POST',
      headers: staffCsrfHeaders(),
      body: { username, password },
      signal,
    })
    if (typeof response.loginRef !== 'string' || typeof response.displayName !== 'string' || typeof response.message !== 'string') {
      throw new ApiError(502, 'INVALID_RESPONSE', 'Staff auth login response is missing loginRef/displayName/message.')
    }
    return {
      loginRef: response.loginRef,
      displayName: response.displayName,
      message: response.message,
    }
  } catch (error) {
    throw rewrapAuthError(error, 'credentials')
  }
}

/**
 * Step 2 of the employee flow: POST /api/staff-auth/verify {loginRef, otp}.
 *
 * On 200, the BFF has set the SIHSESSION cookie. The response carries the
 * display profile (per AuthController.me); the SPA treats it as the
 * authenticated user.
 */
export async function verify(loginRef: string, otp: string, signal?: AbortSignal): Promise<AuthUser> {
  try {
    const response = await request<{ user?: unknown }>(endpoints.staffAuthVerify, {
      method: 'POST',
      headers: staffCsrfHeaders(),
      body: { loginRef, otp },
      signal,
    })
    return mapStaffUser(response.user)
  } catch (error) {
    throw rewrapAuthError(error, 'verify')
  }
}

/**
 * GET /api/staff-auth/me. Returns the current user or null on 401 (no
 * session). Other errors (network, 5xx) propagate as ApiError.
 */
export async function me(signal?: AbortSignal): Promise<AuthUser | null> {
  try {
    const response = await request<BffMeResponse>(endpoints.staffAuthMe, { signal })
    return mapStaffUser(response.user)
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      return null
    }
    throw error
  }
}

/**
 * POST /api/staff-auth/logout. The BFF requires the Spring CSRF cookie-repo
 * header (X-XSRF-TOKEN), explicitly echoed from its XSRF-TOKEN cookie.
 * Rejections propagate: only an accepted response proves BFF session invalidation.
 * The shared client's genuine 401 expiry handling remains in force.
 */
export async function logout(signal?: AbortSignal): Promise<void> {
  await request<unknown>(endpoints.staffAuthLogout, {
    method: 'POST',
    headers: staffCsrfHeaders(),
    signal,
  })
}

function rewrapAuthError(error: unknown, step: AuthStep): AuthApiError {
  if (error instanceof AuthApiError) return error
  if (error instanceof ApiError) {
    const reason = mapAuthErrorCode(error.status, error.code, step)
    return new AuthApiError(error.status, error.code, error.message, reason)
  }
  const message = error instanceof Error ? error.message : String(error)
  return new AuthApiError(0, 'UNREACHABLE', `Cannot reach the staff auth BFF — ${message}`, 'unavailable')
}
